'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useFavoriteGames } from '@/hooks/use-favorite-games';
import {
    getPostLoginAction,
    clearPostLoginAction,
    openDepositSafely,
    POST_LOGIN_ACTION_TYPE,
    type PostLoginAction
} from '@/utils/post-login-action/post-login-action';
import { useGlobalContext } from '@/store/contexts/global-context';
import { useWalletUm } from '@/hooks/use-wallet-um';
import { useStore } from '@/store/store';
import { launchGameWindow } from '@/utils/promotion-game-launch/promotion-game-launch';
import { useLauncherUrlResolution } from '@/hooks/use-launcher-url-resolution';
import { addRegionToLauncherUrl, resolveClgUrl } from '@/hooks/use-promotion-launcher/utils';
import { GameLaunchMode, getGameLaunchMode } from '@/utils/url/game-launch-mode';
import { appendEmbeddedQueryParam } from '@/logic/game-shell/shell-bridge';

export const usePostLoginActionHandler = () => {
    const router = useRouter();
    const { addFavoriteGame } = useFavoriteGames();
    const { isMobile, isXsellWrapper, isAndroidWrapper, isIosWrapper, region, isDesktop } = useGlobalContext();
    const launcherResolution = useLauncherUrlResolution();
    const { triggerDeposit } = useWalletUm();
    const isLoggedIn = useStore((s) => s.userContext.isLoggedIn);
    const addGameWindow = useStore((s) => s.addGameWindow);
    const openEmbeddedGame = useStore((s) => s.embeddedGame.open);

    useEffect(() => {
        if (!isLoggedIn) {
            return;
        }
        const action: PostLoginAction | null = getPostLoginAction();
        if (!action) {
            return;
        }
        clearPostLoginAction();

        let cancelled = false;

        const consume = async () => {
            switch (action.type) {
                case POST_LOGIN_ACTION_TYPE.OPEN_DEPOSIT:
                    await openDepositSafely(triggerDeposit);
                    return;
                case POST_LOGIN_ACTION_TYPE.ADD_FAVORITE:
                    await addFavoriteGame(action.uid);
                    break;
                case POST_LOGIN_ACTION_TYPE.LAUNCH_GAME: {
                    const resolvedUrl = addRegionToLauncherUrl(
                        resolveClgUrl(action.url, launcherResolution, action?.launchMode),
                        region
                    );
                    const launchMode =
                        action.launchMode ??
                        getGameLaunchMode(resolvedUrl, {
                            ...launcherResolution,
                            isMobile,
                            isAndroidWrapper,
                            isIosWrapper,
                            isDesktop
                        });
                    if (launchMode === GameLaunchMode.EMBED) {
                        if (cancelled) {
                            return;
                        }
                        openEmbeddedGame(appendEmbeddedQueryParam(resolvedUrl));
                        // CTA login already hard-reloads once cookies sync; refreshing again races
                        // embedded iframe boot (WebWrapper teardown / launcher fallback close).
                        return;
                    }

                    launchGameWindow(action.url, {
                        isMobile,
                        isDesktop,
                        isXsellWrapper,
                        isAndroidWrapper,
                        addGameWindow,
                        region,
                        launcherResolution
                    });
                    break;
                }
            }

            if (cancelled) {
                return;
            }

            router.refresh();
        };
        consume();

        return () => {
            cancelled = true;
        };
    }, [
        isLoggedIn,
        addFavoriteGame,
        addGameWindow,
        openEmbeddedGame,
        isMobile,
        isXsellWrapper,
        isAndroidWrapper,
        isIosWrapper,
        region,
        isDesktop,
        launcherResolution,
        router,
        triggerDeposit
    ]);
};
